import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ScrollText } from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ThemeToggle';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <div className="fixed top-0 left-0 right-0 border-b bg-background/80 backdrop-blur-sm z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <ThemeToggle />
        </div>
      </div>

      <div className="container mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white">
              <ScrollText className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">Terms of Service</h1>
              <p className="text-muted-foreground">Last updated: February 10, 2025</p>
            </div>
          </div>

          <div className="prose prose-sm md:prose-base max-w-none dark:prose-invert">
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using CodeNest ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service.
            </p>

            <h2>2. Intellectual Property Rights</h2>
            <p>
              The Service and its original content, features, and functionality are owned by CodeNest and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
            </p>

            <h2>3. User Accounts</h2>
            <h3>3.1 Registration</h3>
            <p>
              To use certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
            </p>

            <h3>3.2 Account Security</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
            </ul>

            <h2>4. Subscription Terms</h2>
            <h3>4.1 Plans and Billing</h3>
            <p>
              We offer different subscription plans with varying features and limitations. By selecting a paid subscription, you agree to pay all fees in accordance with the billing terms in effect at the time.
            </p>

            <h3>4.2 Free Tier Limitations</h3>
            <p>
              Free tier accounts are subject to the following limitations:
            </p>
            <ul>
              <li>Maximum of 8 notes</li>
              <li>500 KB total storage</li>
              <li>5,000 character limit per note</li>
              <li>No custom tags (preset tags only)</li>
            </ul>

            <h3>4.3 Cancellation and Refunds</h3>
            <p>
              You may cancel your subscription at any time. Refunds are provided in accordance with applicable law and at our discretion.
            </p>

            <h2>5. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights</li>
              <li>Transmit harmful code or malware</li>
              <li>Engage in unauthorized data collection</li>
              <li>Interfere with the Service's operation</li>
            </ul>

            <h2>6. Content Guidelines</h2>
            <h3>6.1 Your Content</h3>
            <p>
              You retain all rights to the content you post on the Service. By posting content, you grant us a license to use, modify, and display that content in connection with the Service.
            </p>

            <h3>6.2 Prohibited Content</h3>
            <p>
              You may not post content that:
            </p>
            <ul>
              <li>Is illegal or promotes illegal activities</li>
              <li>Is malicious or harmful</li>
              <li>Infringes on others' rights</li>
              <li>Contains malware or exploits</li>
            </ul>

            <h2>7. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account and access to the Service:
            </p>
            <ul>
              <li>For violations of these Terms</li>
              <li>For abusive or fraudulent behavior</li>
              <li>For non-payment of fees</li>
              <li>At our sole discretion</li>
            </ul>

            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, CodeNest shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service.
            </p>

            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless CodeNest and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, or expenses arising from your use of the Service or violation of these Terms.
            </p>

            <h2>10. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of any material changes via email or through the Service.
            </p>

            <h2>11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which CodeNest operates, without regard to its conflict of law provisions.
            </p>

            <h2>12. Contact</h2>
            <p>
              For questions about these Terms, please contact us at{' '}
              <a href="mailto:legal@codenest.dev">support@junaid.xyz</a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;